import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type PropsWithChildren,
  type RefObject,
} from 'react';

import { ENOPOrganType, useIsModalActive, type Point } from '#shared';

import { useAircrafts, useData, useIsIdle } from '../../../data-context';
import { getAirportLabel } from '../../../helpers';
import { useAppDispatch } from '../../../redux-store';
import {
  useSelectCarouselDuration,
  useSelectHighlightedAirports,
  useSelectIsWindRotationOn,
  widgetManagerActions,
} from '../../../widgetManager';

import type { AircraftData, MaoAirport } from '#entities';
import type { OverlayAirport } from '../types';

export interface OverlayContextProps {
  onAircraftSelect: (aircraftId: number) => void;

  airports: OverlayAirport[];
  aircraftsOnFly: AircraftData[];
  aircraftsByTailNumbers: Record<string, AircraftData>;
  handleHighlightAircraft: (registration?: string) => void;
  isInRotation: RefObject<boolean>;
  furtherPaths: Record<
    string,
    {
      organ: ENOPOrganType | null;
      airports: string[];
      points: Point[];
    }
  >;
  trails: Record<
    string,
    {
      organ: ENOPOrganType | null;
      points: Point[];
    }
  >;
}

export const OverlayContext = createContext<OverlayContextProps | undefined>(
  undefined,
);
export interface OverlayProviderProps {
  onAircraftSelect: (aircraftId: number) => void;
}

export const useOverlay = () => {
  const context = useContext(OverlayContext);
  if (!context) {
    throw new Error('useOverlay must be used within an OverlayProvider');
  }
  return context;
};

export const OverlayProvider = ({
  onAircraftSelect,
  children,
}: PropsWithChildren<OverlayProviderProps>) => {
  const dispatch = useAppDispatch();
  const { metarsData, tafsRawData } = useData();
  const aircrafts = useAircrafts();
  // const { projection, projectionId } = useWindContext();

  const rotationInterval = useSelectCarouselDuration();
  const isIdle = useIsIdle();
  const isModalActive = useIsModalActive();
  // Wind map rotation is controlled by its OWN flag, independent of the
  // main-map "Rotate cases" setting (TSMED-1172).
  const isWindRotationOn = useSelectIsWindRotationOn();

  const { aircraftsOnFly, airportsById, aircraftsByTailNumbers } =
    useMemo(() => {
      const aircraftsOnFly: Record<AircraftData['Id'], AircraftData> = {};
      const aircraftsOrphan: Record<AircraftData['Id'], AircraftData> = {};
      const aircraftsByTailNumbers: Record<number, AircraftData> = {};
      const airportsById = aircrafts.reduce<Record<string, OverlayAirport>>(
        (acc, aircraftData) => {
          aircraftsByTailNumbers[aircraftData.registration] = aircraftData;
          if (aircraftData.airports.length) {
            for (const aircraft of aircraftData.airports) {
              const { id, iata, icao, airport } = aircraft;
              const metar = (icao && metarsData[icao]) || null;
              const taf = (icao && tafsRawData?.[icao]) || null;

              if (!acc[id]) {
                acc[id] = {
                  id,
                  longitude: airport.Longitude,
                  latitude: airport.Latitude,
                  label: getAirportLabel(iata, icao),
                  IATA: iata,
                  ICAO: icao,
                  metar,
                  taf,
                  aircrafts: [] as string[],
                };
              }
            }
          } else {
            const nextAirport = aircraftData.airportLastKnown;
            if (nextAirport) {
              const { IATA, ICAO, Id, Latitude, Longitude } = nextAirport;

              const metar = (ICAO && metarsData[ICAO]) || null;
              const taf = (ICAO && tafsRawData?.[ICAO]) || null;

              if (!acc[Id]) {
                acc[Id] = {
                  id: Id,
                  longitude: Longitude,
                  latitude: Latitude,
                  label: getAirportLabel(IATA, ICAO),
                  IATA,
                  ICAO,
                  metar,
                  taf,
                  aircrafts: [] as string[],
                };
              }
            } else {
              aircraftsOrphan[aircraftData.registration] = aircraftData;
            }
          }
          if (aircraftData.isOnFlight) {
            aircraftsOnFly[aircraftData.Id] = aircraftData;
          } else if (aircraftData.airportLastKnown) {
            const ICAO = aircraftData.airportLastKnown.ICAO;
            const IATA = aircraftData.airportLastKnown.IATA;
            const id = aircraftData.airportLastKnown.Id;
            const metar = (ICAO && metarsData[ICAO]) || null;
            const taf = (ICAO && tafsRawData?.[ICAO]) || null;

            if (!acc[id]) {
              acc[id] = {
                id,
                longitude: aircraftData.airportLastKnown.Longitude,
                latitude: aircraftData.airportLastKnown.Latitude,
                label: getAirportLabel(IATA, ICAO),
                IATA,
                ICAO,
                metar,
                taf,
                aircrafts: [] as string[],
              };
            }
            acc[id].aircrafts.push(aircraftData.registration);
          }

          return acc;
        },
        {},
      );

      return {
        aircraftsOnFly: Object.values(aircraftsOnFly),
        airportsById,
        aircraftsByTailNumbers,
        aircraftsOrphan: Object.values(aircraftsOrphan),
      };
    }, [aircrafts, metarsData, tafsRawData]);

  const trails = useMemo(
    () =>
      aircrafts.reduce<
        Record<string, { organ: ENOPOrganType | null; points: Point[] }>
      >((acc, { ActiveTrail, registration, actualOrgan }) => {
        acc[registration] = {
          organ: actualOrgan,
          points: ActiveTrail.map(
            ({ Latitude, Longitude }) => [Longitude, Latitude] as Point,
          ),
        };
        return acc;
      }, {}),
    [aircrafts],
  );
  const furtherPaths = useMemo(
    () =>
      aircrafts.reduce<
        Record<
          string,
          { organ: ENOPOrganType | null; airports: string[]; points: Point[] }
        >
      >((acc, { tripActual, registration, actualOrgan }) => {
        const furtherAirports =
          tripActual?.Legs.reduce<MaoAirport[]>((acc, leg) => {
            if (!leg.Movement?.IsOnBlocks) {
              if (acc.length === 0) {
                acc.push(leg.DepartureAirport);
              }
              acc.push(leg.ArrivalAirport);
            }
            return acc;
          }, []) ?? [];

        const icaos = furtherAirports.map(({ ICAO }) => ICAO ?? '---');
        const furtherPath = furtherAirports.map(
          ({ Longitude, Latitude }) => [Longitude, Latitude] as Point,
        );
        acc[registration] = {
          airports: icaos,
          points: furtherPath,
          organ: actualOrgan,
        };
        return acc;
      }, {}),

    [aircrafts],
  );
  const handleHighlightAircraft = useCallback(
    (registration?: string) => {
      // Update highlighted aircraft registration in state
      dispatch(
        widgetManagerActions.setHighlightedAircraftRegistration(
          registration ?? null,
        ),
      );

      // Determine which airports to highlight based on aircraft registration
      const airportIdsToHighlight = (() => {
        if (!registration) {
          return [];
        }

        // Get trip airports (future path) from furtherPaths
        const tripAirportICAOs = furtherPaths[registration]?.airports ?? [];

        if (tripAirportICAOs.length > 0) {
          // Convert ICAO codes to airport IDs for highlighting
          return Object.values(airportsById)
            .filter(airport => tripAirportICAOs.includes(airport.ICAO ?? ''))
            .map(airport => String(airport.id));
        }

        // Fallback: If no trip airports, highlight only the last known airport
        const lastKnownAirport = aircrafts.find(
          aircraft => aircraft.registration === registration,
        )?.airportLastKnown;

        return lastKnownAirport ? [lastKnownAirport.Id.toString()] : [];
      })();

      // Update highlighted airports in state
      dispatch(
        widgetManagerActions.setHighlightedAirports(airportIdsToHighlight),
      );
    },
    [airportsById, aircrafts, dispatch, furtherPaths],
  );

  const highlightedAircraftRef = useRef(0);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const isInRotation = useRef(false);

  useEffect(() => {
    const onCaseAircrafts = aircrafts.filter(a => !!a.actualOrgan);
    const shouldRotate = isWindRotationOn && isIdle && !isModalActive;
    if (!shouldRotate || onCaseAircrafts.length < 2) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Reset the rotated highlight back to default when rotation stops
      // (toggle off / no longer idle / fewer than 2 cases). This lives in the
      // effect body — NOT the cleanup — because the cleanup runs on every
      // dependency change (e.g. a data refresh while still rotating); clearing
      // there would wipe the active highlight mid-rotation. The cleanup
      // therefore must NOT reset `isInRotation`, or this check would never see
      // that a rotation was in progress.
      if (isInRotation.current) {
        handleHighlightAircraft();
      }
      isInRotation.current = false;
      // Reset the cycle index so the next rotation starts from the first case
      // rather than resuming mid-sequence.
      highlightedAircraftRef.current = 0;
      return;
    }
    // Advance the rotation by one step. Shared by the immediate prime below and
    // the interval so the first tick and every later tick stay in lock-step.
    const highlightNextCase = () => {
      if (!onCaseAircrafts.length) {
        return;
      }
      const index = highlightedAircraftRef.current % onCaseAircrafts.length;
      handleHighlightAircraft(onCaseAircrafts[index].registration);
      isInRotation.current = true;
      highlightedAircraftRef.current++;
    };

    // Prime the first highlight synchronously so the wind map's rotation starts
    // at the same time as the main map instead of one interval later
    // (TSMED-1068). `setInterval` alone fires its first callback only after a
    // full `rotationInterval`, leaving the wind map blank during tick #1 on the
    // main map. Guard on `isInRotation` so this fires only on a genuine
    // (re)start: a data refresh mid-rotation re-runs this effect, and re-priming
    // there would restart the cadence and re-highlight the first case.
    if (!isInRotation.current) {
      highlightNextCase();
    }

    intervalRef.current = setInterval(highlightNextCase, rotationInterval);
    return () => {
      // Only tear down the timer here. Do NOT reset `isInRotation`: React runs
      // this cleanup before the next effect body, which inspects `isInRotation`
      // to decide whether to clear the rotated highlight on stop.
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    aircrafts,
    isWindRotationOn,
    handleHighlightAircraft,
    isIdle,
    isModalActive,
    rotationInterval,
  ]);

  // Clear a rotated highlight if the wind map unmounts mid-rotation. The main
  // effect's stop branch only runs on dependency changes, not on unmount, so
  // without this a stale highlight would linger in Redux (it survives the
  // component) and dim the map on the next mount until rotation resumed
  // (TSMED-1172). Runs only on unmount — `dispatch` is stable — so it cannot
  // wipe the active highlight mid-rotation. Dispatches directly rather than via
  // `handleHighlightAircraft` to avoid depending on its changing identity.
  useEffect(
    () => () => {
      if (isInRotation.current) {
        dispatch(widgetManagerActions.setHighlightedAircraftRegistration(null));
        dispatch(widgetManagerActions.setHighlightedAirports([]));
      }
      // Always reset the rotation bookkeeping here — unconditionally, NOT inside
      // the `isInRotation` guard above. The refs survive the component, so under
      // StrictMode (which unmounts then remounts in dev) a stale
      // `isInRotation`/index would make the main effect's prime guard skip the
      // re-prime, leaving the map blank and re-opening TSMED-1068. Resetting
      // unconditionally lets any remount start fresh.
      isInRotation.current = false;
      highlightedAircraftRef.current = 0;
    },
    [dispatch],
  );

  const highlightedAirports = useSelectHighlightedAirports();
  const airports = useMemo(
    () =>
      Object.values(airportsById).toSorted(({ id }) =>
        highlightedAirports.includes(id ? id.toString() : '') ? 1 : -1,
      ),
    [airportsById, highlightedAirports],
  );
  const ctx = useMemo(
    (): OverlayContextProps => ({
      onAircraftSelect,
      airports,
      aircraftsOnFly,
      aircraftsByTailNumbers,
      handleHighlightAircraft,
      furtherPaths,
      trails,
      isInRotation,
    }),
    [
      aircraftsByTailNumbers,
      aircraftsOnFly,
      airports,
      furtherPaths,
      handleHighlightAircraft,
      onAircraftSelect,
      trails,
    ],
  );
  return (
    <OverlayContext.Provider value={ctx}>{children}</OverlayContext.Provider>
  );
};
